import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const LINKS = [
  { to: "/", label: "HOME", testid: "nav-home-link" },
  { to: "/solutions", label: "SOLUTIONS", testid: "nav-solutions-link" },
  { to: "/products", label: "PRODUCTS", testid: "nav-products-link" },
  { to: "/projects", label: "PROJECTS", testid: "nav-projects-link" },
  { to: "/about", label: "ABOUT", testid: "nav-about-link" },
  { to: "/contact", label: "CONTACT", testid: "nav-contact-link" },
];

const LANGS = ["EN", "FR", "ES"];

export const Logo = ({ light = false }) => (
  <span className="flex items-center gap-2.5">
    <span className="relative flex h-9 w-9 items-center justify-center bg-[#0B132B] font-display text-sm font-extrabold tracking-tight text-white">
      BC
      <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-[#0D9488]" />
    </span>
    <span className={`font-display text-base font-bold tracking-[0.18em] ${light ? "text-white" : "text-[#0B132B]"}`}>
      BUY CARIBBEAN
    </span>
  </span>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("EN");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const pickLang = (l) => {
    if (l !== "EN") {
      toast.info(
        l === "FR"
          ? "Version française — bientôt disponible. The English edition is live."
          : "Versión en español — próximamente. The English edition is live."
      );
      return;
    }
    setLang(l);
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-[#F8F7F4]/85 backdrop-blur-md transition-all duration-300 ${
        scrolled
          ? "border-[#0B132B]/10 shadow-[0_10px_36px_-20px_rgba(11,19,43,0.4)]"
          : "border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div
        className={`mx-auto flex items-center justify-between px-5 transition-all duration-300 lg:px-8 ${
          scrolled ? "h-16" : "h-[76px]"
        }`}
      >
        <Link to="/" data-testid="nav-logo-link" aria-label="BUY CARIBBEAN home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `nav-link text-[12px] font-bold tracking-[0.16em] transition-colors ${
                  isActive ? "nav-link-active text-[#0B132B]" : "text-[#0B132B]/55 hover:text-[#0B132B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <div className="flex items-center gap-1 text-xs font-bold tracking-widest" data-testid="language-selector">
            {LANGS.map((l, i) => (
              <span key={l} className="flex items-center gap-1">
                {i > 0 && <span className="text-[#0B132B]/25">|</span>}
                <button
                  type="button"
                  onClick={() => pickLang(l)}
                  data-testid={`lang-${l.toLowerCase()}-button`}
                  className={`px-1 transition-colors ${
                    lang === l ? "text-[#0D9488]" : "text-[#0B132B]/50 hover:text-[#0B132B]"
                  }`}
                >
                  {l}
                </button>
              </span>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="nav-submit-project-button"
            className="btn-sweep btn-sweep-teal group inline-flex items-center gap-2 bg-[#0B132B] px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-white"
          >
            SUBMIT YOUR PROJECT
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#0B132B]/10 bg-[#F8F7F4] px-5 pb-6 pt-4 lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-bold tracking-[0.14em] ${
                    isActive ? "bg-[#0B132B]/5 text-[#0D9488]" : "text-[#0B132B]/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 flex items-center gap-2 px-3 text-xs font-bold tracking-widest" data-testid="mobile-language-selector">
            {LANGS.map((l, i) => (
              <span key={l} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#0B132B]/25">|</span>}
                <button type="button" onClick={() => pickLang(l)} data-testid={`mobile-lang-${l.toLowerCase()}-button`} className={lang === l ? "text-[#0D9488]" : "text-[#0B132B]/50"}>
                  {l}
                </button>
              </span>
            ))}
          </div>
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-submit-project-button"
            className="mt-4 flex items-center justify-center gap-2 bg-[#0B132B] px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.12em] text-white"
          >
            SUBMIT YOUR PROJECT
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </header>
  );
}
